import React from "react";
import { commitMutation, graphql } from "react-relay";
import { useHistory } from "react-router";

import {
	faCheck,
	faFileSearch,
	faHashtag,
	faInfoCircle,
	faLockAlt,
	faSearch,
	faTimes,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faPlus } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import { showFeatureModal, UpgradeIcon } from "~/billing";
import { useDebouncedValue } from "~/lib/hooks";
import { confirm } from "~/lib/prompts";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { withSuspense } from "~/lib/suspense";
import { humanizeDateAgo, nGetText } from "~/lib/utils";
import { environment } from "~/models";
import { CreateChannelModal } from "~/recordings/CreateEditChannelModal/CreateEditChannelModal";
import { eventEmitter as recordingsEventEmitter } from "~/recordings/events";
import { Button, useModals } from "~/ui";
import AvatarList from "~/ui/avatar/AvatarList";
import { Modal } from "~/ui/legacyFellowIndex";
import LoadingSpinner from "~/ui/LoadingSpinner";
import toast, { ToastActionButton } from "~/ui/toast";

import type { BrowseChannelsModalJoinChannelMutation } from "~/__generated__/BrowseChannelsModalJoinChannelMutation.graphql";
import type { BrowseChannelsModalLeaveChannelMutation } from "~/__generated__/BrowseChannelsModalLeaveChannelMutation.graphql";
import type { BrowseChannelsModalQuery as BrowseChannelsModalQueryType } from "~/__generated__/BrowseChannelsModalQuery.graphql";

import parentStyles from "../RecordingsPage.module.css";
import styles from "./BrowseChannelModal.module.css";

type ModalBodyProps = {
	search: string;
	fetchKey: number;
	refetchChannels: () => void;
	selectChannel: (channel: BrowseChannelType) => void;
	redirectToLibrary: boolean;
};

graphql`
	fragment BrowseChannelsModalChannel on Channel {
		id
		name
		description
		isMember
		isPrivate
		isWorkspaceWide
		lastPostedAt
		postsCount
		members {
			id
			email
			fullName
			image
		}
		activeSmartTrackers {
			name
			triggerPhrasesOrWords
		}
	}
`;

const channelsQuery = graphql`
	query BrowseChannelsModalQuery($search: String) {
		viewer {
			channels(excludePersonal: true, search: $search) {
				...BrowseChannelsModalChannel @relay(mask: false)
			}
			autoPublishRecordingSettings {
				id
				enabled
				externalRecordingsChannel {
					id
				}
				internalRecordingsChannel {
					id
				}
			}
		}
	}
`;

export type BrowseChannelType = BrowseChannelsModalQueryType["response"]["viewer"]["channels"][number];
export type ChannelMember = BrowseChannelsModalQueryType["response"]["viewer"]["channels"][number]["members"][number];
type AutoPublishSettings = BrowseChannelsModalQueryType["response"]["viewer"]["autoPublishRecordingSettings"];
export type ChannelActiveSmartTrackerType =
	BrowseChannelsModalQueryType["response"]["viewer"]["channels"][number]["activeSmartTrackers"][number];

export const joinChannelMutation = graphql`
	mutation BrowseChannelsModalJoinChannelMutation($channelId: ID!) {
		joinChannel(channelId: $channelId) {
			channel {
				id
				isMember
				members {
					id
					email
					fullName
					image
				}
			}
		}
	}
`;

export async function leaveConfirmation(channel: { isPrivate: boolean; name: string }, isUsedInAutoPublish: boolean) {
	let message = (
		<div>
			Are you sure you want to leave the channel <b>&quot;{channel.name}&quot;</b>?
		</div>
	);
	if (channel.isPrivate) {
		message = (
			<div>
				<b>&quot;{channel.name}&quot; is a private channel,</b> you will only be able to rejoin via an invite from an
				existing channel member.
			</div>
		);
	}
	if (isUsedInAutoPublish) {
		message = (
			<>
				{message}
				<div>
					<br />
					You will not be able to automatically publish recordings to this channel anymore.
				</div>
			</>
		);
	}

	const wannaLeave = await confirm(message, {
		yesText: "Yes, leave",
		title: channel.isPrivate ? "Leave private channel?" : "Want to leave the channel?",
		yesProps: { variant: channel.isPrivate ? "destructive" : "primary" },
	});

	return !!wannaLeave;
}

export const leaveChannelMutation = graphql`
	mutation BrowseChannelsModalLeaveChannelMutation($channelId: ID!) {
		leaveChannel(channelId: $channelId) {
			channel {
				id
				isMember
				members {
					id
					email
					fullName
					image
				}
			}
		}
	}
`;

function ChannelJoined() {
	return (
		<div className={styles.channelJoined}>
			<FontAwesomeIcon icon={faCheck} />
			<div>Joined</div>
		</div>
	);
}

function JoinOrLeaveButton({
	channel,
	refetchChannels,
	autoPublishSettings,
	redirectToLibrary,
}: {
	channel: BrowseChannelType;
	refetchChannels: () => void;
	autoPublishSettings: AutoPublishSettings;
	redirectToLibrary: boolean;
}) {
	const [isLoading, setIsLoading] = React.useState(false);
	const history = useHistory();

	const joinChannel = React.useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			setIsLoading(true);
			event.stopPropagation();

			commitMutation<BrowseChannelsModalJoinChannelMutation>(environment, {
				mutation: joinChannelMutation,
				variables: { channelId: channel.id },
				onCompleted: () => {
					setIsLoading(false);
					const channelName = redirectToLibrary ? ` "${channel.name}"` : "";
					const toastId = toast.success(`Joined the channel${channelName}!`, {
						actions: redirectToLibrary ? (
							<>
								<ToastActionButton
									// eslint-disable-next-line fellow/no-inline-disable
									// eslint-disable-next-line react/jsx-no-bind
									onClick={() => {
										toast.dismiss(toastId);
										history.push(`/library/c/${channel.id}/`);
									}}
								>
									View channel
								</ToastActionButton>
							</>
						) : (
							<></>
						),
					});
					dispatchAnalyticsTrack("Join channel", { localtion: "BrowseChannelsModal" });
					recordingsEventEmitter.emit("Channels.refetch");
				},
			});
		},
		[channel.id, channel.name, history, redirectToLibrary],
	);

	const leaveChannel = React.useCallback(
		async (event: React.MouseEvent<HTMLButtonElement>) => {
			event.stopPropagation();

			let message = (
				<div>
					<b>&quot;{channel.name}&quot; is a private channel,</b> you will only be able to rejoin via an invite from an
					existing channel member.
				</div>
			);
			const autoPublishSettingUsesChannel =
				channel.id === autoPublishSettings?.externalRecordingsChannel?.id ||
				channel.id === autoPublishSettings?.internalRecordingsChannel?.id;

			if (autoPublishSettingUsesChannel) {
				const autoPublishMessage = (
					<div>You will not be able to automatically publish recordings to this channel anymore.</div>
				);
				if (channel.isPrivate) {
					message = (
						<>
							{message}
							<br />
							{autoPublishMessage}
						</>
					);
				} else {
					const wannaLeave = await confirm(autoPublishMessage, {
						yesText: "Yes, leave",
						title: "Leave channel?",
						yesProps: { variant: "destructive" },
					});
					if (!wannaLeave) return;
				}
			}

			if (channel.isPrivate) {
				const wannaLeave = await confirm(message, {
					yesText: "Yes, leave",
					title: "Leave private channel?",
					yesProps: { variant: "destructive" },
				});
				if (!wannaLeave) return;
			}

			setIsLoading(true);

			commitMutation<BrowseChannelsModalLeaveChannelMutation>(environment, {
				mutation: leaveChannelMutation,
				variables: { channelId: channel.id },
				onCompleted: () => {
					setIsLoading(false);
					toast.success("Left the channel!");
					if (channel.isPrivate) {
						refetchChannels();
					} else {
						// Higher-level refetch for swimlanes
						recordingsEventEmitter.emit("Channels.refetch");
					}

					dispatchAnalyticsTrack("Leave channel", { localtion: "BrowseChannelsModal" });
					recordingsEventEmitter.emit("Channels.left", channel.id);
				},
			});
		},
		[channel, refetchChannels, autoPublishSettings],
	);

	if (channel.isMember) {
		return (
			<Button
				variant="destructive"
				size="slim"
				className={styles.joinLeaveButton}
				disabled={isLoading}
				onClick={leaveChannel}
			>
				Leave
			</Button>
		);
	}

	return (
		<Button variant="primary" size="slim" className={styles.joinLeaveButton} disabled={isLoading} onClick={joinChannel}>
			Join
		</Button>
	);
}

interface ChannelIconProps {
	isPrivate: boolean;
	readOnly?: boolean;
}

export function ChannelIcon({ isPrivate, readOnly }: ChannelIconProps) {
	const classNames = [styles.channelIcon];
	if (readOnly) {
		classNames.push(styles.channelIconReadOnly);
	}
	return (
		<div className={cx(classNames)}>
			{isPrivate ? <FontAwesomeIcon icon={faLockAlt} /> : <FontAwesomeIcon icon={faHashtag} />}
		</div>
	);
}

function ChannelRow({
	channel,
	refetchChannels,
	autoPublishSettings,
	selectChannel,
	redirectToLibrary,
}: {
	channel: BrowseChannelType;
	refetchChannels: () => void;
	autoPublishSettings: AutoPublishSettings;
	selectChannel: (channel: BrowseChannelType) => void;
	redirectToLibrary: boolean;
}) {
	const onClick = React.useCallback(() => {
		selectChannel(channel);
	}, [channel, selectChannel]);

	return (
		<div className={styles.channelRow} onClick={onClick}>
			<div className={styles.channelInfoWrapper}>
				<div className={styles.channelInfo}>
					<ChannelIcon isPrivate={channel.isPrivate} />
					<div className={styles.channelLabels}>
						<div className={styles.channelTitle} title={channel.name}>
							{channel.name}
						</div>
						<div className={styles.channelSubtitle}>
							{`${channel.postsCount} ${nGetText(channel.postsCount, "file")}`}
							{channel.lastPostedAt && (
								<span>
									<span className={styles.channelEllipsis}>•</span>
									{`Updated ${humanizeDateAgo(channel.lastPostedAt)}`}
								</span>
							)}
						</div>
					</div>
				</div>
			</div>

			<div className={styles.channelMembers}>
				{channel.isWorkspaceWide ? (
					<span className={styles.textDescription}>Everyone</span>
				) : channel.members.length == 0 ? (
					<span className={styles.textDescription}>No members</span>
				) : (
					<AvatarList
						maxLength={3}
						users={channel.members}
						isDetailsTooltipInteractive={false}
						detailsTooltipPlacement="top"
					/>
				)}
			</div>

			<div className={styles.channelExtra}>
				{channel.isMember && <ChannelJoined />}
				<JoinOrLeaveButton
					channel={channel}
					refetchChannels={refetchChannels}
					autoPublishSettings={autoPublishSettings}
					redirectToLibrary={redirectToLibrary}
				/>
			</div>
		</div>
	);
}

function ModalBody({ search, fetchKey, refetchChannels, selectChannel, redirectToLibrary }: ModalBodyProps) {
	const response = useLazyLoadQuery<BrowseChannelsModalQueryType>(
		channelsQuery,
		{ search },
		{ fetchPolicy: "store-and-network", fetchKey },
	);
	const channels = response.viewer.channels;
	const autoPublishSettings = response.viewer.autoPublishRecordingSettings;

	if (channels.length === 0) {
		return (
			<div className={styles.modalBody}>
				<div className={styles.emptyStateWrapper}>
					<div className={styles.noResultsIcon}>
						<FontAwesomeIcon icon={faFileSearch} />
					</div>

					<div className={styles.noResultsText}>No results found</div>
					<div className={styles.noResultsSubtext}>Try different keywords or filters.</div>
				</div>
			</div>
		);
	}

	return (
		<div className={styles.modalBody}>
			<div className={styles.channelsTable}>
				<div className={styles.channelsHeader}>
					<div className={styles.channelsColumn}>Channel name</div>
					<div className={styles.channelsColumn}>Members</div>
					<div className={styles.channelsColumn}>Visibility</div>
				</div>
				<div className={styles.channelsSeparator}>
					<div className={styles.channelsSeparatorColumn} />
				</div>
				{channels.map(channel => (
					<ChannelRow
						key={channel.id}
						channel={channel}
						refetchChannels={refetchChannels}
						selectChannel={selectChannel}
						autoPublishSettings={autoPublishSettings}
						redirectToLibrary={redirectToLibrary}
					/>
				))}
			</div>
		</div>
	);
}

const ModalBodyWithSuspense = withSuspense(<LoadingSpinner dimmer={false} delay={1000} />, ModalBody);

type BrowseChannelsModalProps = {
	canCreateChannels: boolean;
	onClose: () => void;
	selectChannel: (channel: BrowseChannelType) => void;
	autoPublishSettings?: AutoPublishSettings;
	redirectToLibrary?: boolean;
};

export default function BrowseChannelsModal({
	onClose,
	canCreateChannels,
	autoPublishSettings,
	selectChannel,
	redirectToLibrary = false,
}: BrowseChannelsModalProps) {
	const [search, setSearch] = React.useState("");
	const [fetchKey, setFetchKey] = React.useState(0);
	const debouncedSearch = useDebouncedValue(search, 300);
	const modals = useModals();

	const onSearchChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setSearch(e.target.value);
	}, []);

	const onClearSearch = React.useCallback(() => {
		setSearch("");
	}, []);

	const refetchChannels = React.useCallback(() => {
		setFetchKey(prev => prev + 1);
		recordingsEventEmitter.emit("Channels.refetch");
	}, []);

	const openAddChannelModal = React.useCallback(() => {
		if (!canCreateChannels) {
			showFeatureModal("channels");
			return;
		}

		dispatchAnalyticsTrack("Open add channel modal", { location: "BrowseChannelsModal" });
		modals.open(({ close }) => (
			<CreateChannelModal onClose={close} refetchChannels={refetchChannels} autoPublishSettings={autoPublishSettings} />
		));
	}, [canCreateChannels, modals, refetchChannels, autoPublishSettings]);

	const onSelectChannel = React.useCallback(
		(channel: BrowseChannelType) => {
			selectChannel(channel);
			onClose();
		},
		[onClose, selectChannel],
	);

	return (
		<Modal onClose={onClose} width="640px" height="540px">
			<Modal.Header onClose={onClose} noBottomBorder>
				<Modal.Title>
					Browse channels
					<div className={styles.subheader}>View channels in your workspace</div>
				</Modal.Title>
				<Modal.HeaderActions>
					<Button
						variant={canCreateChannels ? "primary" : "billing"}
						className={styles.createChannelButton}
						onClick={openAddChannelModal}
					>
						{canCreateChannels ? <FontAwesomeIcon fixedWidth icon={faPlus} /> : <UpgradeIcon inheritColor />}
						New Channel
					</Button>
				</Modal.HeaderActions>
			</Modal.Header>

			<Modal.Body textOnly>
				<div className={parentStyles.searchWrapper}>
					<label className={cx(parentStyles.searchInput, parentStyles.searchInputWide)}>
						<FontAwesomeIcon icon={faSearch} className={parentStyles.searchIcon} />
						<input type="text" value={search} placeholder="Search channels..." onChange={onSearchChange} />
						{search && (
							<div
								className={cx(parentStyles.crossIconWrapper, parentStyles.crossIconWrapperWide)}
								onClick={onClearSearch}
							>
								<FontAwesomeIcon icon={faTimes} />
							</div>
						)}
					</label>
				</div>

				<ModalBodyWithSuspense
					search={debouncedSearch}
					fetchKey={fetchKey}
					refetchChannels={refetchChannels}
					selectChannel={onSelectChannel}
					redirectToLibrary={redirectToLibrary}
				/>
			</Modal.Body>

			<Modal.Footer className={styles.modalFooter}>
				<a
					href="https://help.fellow.app/en/articles/9518898-recording-channels"
					target="_blank"
					rel="noreferrer"
					className={styles.helpArticleLink}
				>
					<FontAwesomeIcon icon={faInfoCircle} />
					<span>Learn more about channels in Fellow</span>
				</a>

				<Button variant="primary" onClick={onClose}>
					Done
				</Button>
			</Modal.Footer>
		</Modal>
	);
}
